import { useQuery, useQueryClient } from "@tanstack/react-query"
import { httpSharePointRepository } from "../../data/repositories/sharepoint-repository"
import type { GraphItem } from "../../domain/entities/sharepoint"

const repo = httpSharePointRepository

export const useSharePointSites = (connectionId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["sharepoint", connectionId, "sites"],
    queryFn: () => repo.listSites(connectionId as string),
    enabled: enabled && !!connectionId,
  })

export const useSharePointSiteDrives = (
  connectionId: string | null,
  siteId: string | null,
) =>
  useQuery({
    queryKey: ["sharepoint", connectionId, "site-drives", siteId],
    queryFn: () => repo.listSiteDrives(connectionId as string, siteId as string),
    enabled: !!connectionId && !!siteId,
  })

export const useSharePointShared = (connectionId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["sharepoint", connectionId, "shared"],
    queryFn: () => repo.listShared(connectionId as string),
    enabled: enabled && !!connectionId,
  })

export const useSharePointOneDrive = (connectionId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["sharepoint", connectionId, "onedrive"],
    queryFn: () => repo.listOneDrive(connectionId as string),
    enabled: enabled && !!connectionId,
  })

export const useSharePointChildren = (
  connectionId: string | null,
  driveId: string | null,
  itemId: string | null,
) =>
  useQuery({
    queryKey: ["sharepoint", connectionId, "children", driveId, itemId],
    queryFn: () =>
      repo.listChildren(connectionId as string, driveId as string, itemId as string),
    enabled: !!connectionId && !!driveId && !!itemId,
  })

// Imperative fetch of *every* child page of a folder, cached like a query.
// Used by "select all", which must reach files beyond the first browse page.
export const useSharePointChildrenAllFetcher = (connectionId: string | null) => {
  const qc = useQueryClient()
  return (driveId: string, itemId: string): Promise<GraphItem[]> =>
    qc.fetchQuery({
      queryKey: ["sharepoint", connectionId, "children-all", driveId, itemId],
      queryFn: () => repo.listChildren(connectionId as string, driveId, itemId, true),
    })
}

export const useSharePointSearch = (
  connectionId: string | null,
  driveId: string | null,
  itemId: string | null,
  q: string,
) =>
  useQuery({
    queryKey: ["sharepoint", connectionId, "search", driveId, itemId, q],
    queryFn: () =>
      repo.searchItems(connectionId as string, driveId as string, itemId as string, q),
    enabled: !!connectionId && !!driveId && !!itemId && q.trim().length > 0,
  })
